import {
  AgaveCostCode,
  AgaveCostType,
  AgaveGlCode,
  AgaveProject,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveEmployeeEntity,
} from 'API';

// Sage Intacct query response type
export interface SageIntacctQueryRequestBody {
  readByQuery: {
    object: string;
    query: string;
    fields: string;
  };
}

// Sage Intacct expense args to be more consistent with the other expense args
export interface SageIntacctExpenseArgs {
  paymentNumber: string;
  amount: string;
  paymentDate: string | null;
  note: string | null;
  merchantName: string | null;
  last4: string | null;
  agaveCostCode: AgaveCostCode | null;
  agaveCostCodeId: string | null;
  agaveCostType: AgaveCostType | null;
  agaveCostTypeId: string | null;
  agaveGlCode: AgaveGlCode | null;
  agaveGlCodeId: string | null;
  agaveProject: AgaveProject | null;
  agaveProjectId: string | null;
  agaveVendor: AgaveVendor | null;
  agaveVendorId: string | null;
  agaveLocation: AgaveLocation | null;
  agaveLocationId: string | null;
  agaveDepartment: AgaveDepartment | null;
  agaveDepartmentId: string | null;
  agaveEmployee: AgaveEmployeeEntity | null;
  agaveEmployeeId: string | null;
}

// Note: sage300Util has been moved to src/app/services/agave/erp/posting/sage300Posting.ts

export const SAGE_INTACCT_SPEEDCHAIN_CREDIT_CARD_NAME = 'SPEEDCHAIN' as const;

export const sageIntacctUtil = {
  speedchainCreditCardName: SAGE_INTACCT_SPEEDCHAIN_CREDIT_CARD_NAME,

  extractVendorId: (rawJson: string | null | undefined) => {
    if (!rawJson) return null;
    return JSON.parse(rawJson)?.operation?.result?.data?.vendor?.VENDORID;
  },

  // Get SPEEDCHAIN credit card by description
  getSpeedchainCardQuery: (): SageIntacctQueryRequestBody => {
    return {
      readByQuery: {
        object: 'CREDITCARD',
        query: "DESCRIPTION = 'Speedchain'",
        fields: '*',
      },
    };
  },

  // Build Sage Intacct credit card transaction
  buildCCTransaction: (
    args: SageIntacctExpenseArgs,
    chargeCardId = SAGE_INTACCT_SPEEDCHAIN_CREDIT_CARD_NAME,
    intacctProjectsNotLicensed = false
  ) => {
    const {
      paymentNumber,
      paymentDate,
      amount,
      note,
      merchantName,
      last4,
      agaveGlCode,
      agaveProject,
      agaveCostType,
      agaveCostCode,
      agaveVendor,
      agaveLocation,
      agaveDepartment,
      agaveEmployee,
    } = args;

    // Parse payment date for year/month/day (avoid timezone issues)
    let year: string, month: string, day: string;

    if (paymentDate && /^\d{4}-\d{2}-\d{2}$/.test(paymentDate)) {
      const dateParts = paymentDate.split('-');
      year = dateParts[0];
      month = dateParts[1];
      day = dateParts[2];
    } else {
      const now = new Date();
      year = now.getFullYear().toString();
      month = (now.getMonth() + 1).toString().padStart(2, '0');
      day = now.getDate().toString().padStart(2, '0');
    }

    // Build description once to avoid duplication
    const description = last4
      ? `Card last4: ${last4} - ${note || 'Speedchain Expense'}`
      : note || 'Speedchain Expense';

    // SEE-1112: paidols without the Projects module code against GL dimensions,
    // so route cost code -> classid (Class dimension) and cost type -> itemid
    // (Item dimension) instead of the Projects-module taskid/costtypeid fields.
    // projectid stays as coded: for these paidols projects come from GLDETAIL
    // dimension values, which Intacct still accepts on ccpayitem.
    const costCodeValue = agaveCostCode?.code || '';
    const costTypeValue = agaveCostType?.source_id || agaveCostType?.code || '';
    const taskid = intacctProjectsNotLicensed ? '' : costCodeValue;
    const costtypeid = intacctProjectsNotLicensed ? '' : costTypeValue;
    const itemid = intacctProjectsNotLicensed ? costTypeValue : '';
    const classid = intacctProjectsNotLicensed ? costCodeValue : '';

    // Build credit card transaction item
    const ccPayItem: Record<string, string> = {
      glaccountno: agaveGlCode?.number || agaveGlCode?.source_id || '',
      description,
      paymentamount: amount,
      departmentid: agaveDepartment?.source_id || '',
      locationid: agaveLocation?.source_id || '',
      customerid: '',
      vendorid: agaveVendor?.code || '',
      employeeid: agaveEmployee?.source_id || '',
      projectid: agaveProject?.number || agaveProject?.source_id || '',
      taskid,
      costtypeid,
      itemid,
      classid,
      contractid: '',
      warehouseid: '',
      customfields: '',
    };

    return {
      record_cctransaction: {
        chargecardid: chargeCardId,
        paymentdate: {
          year,
          month,
          day,
        },
        referenceno: paymentNumber,
        payee: merchantName || 'Speedchain',
        description,
        currency: 'USD',
        exchratetype: 'Intacct Daily Rate',
        ccpayitems: {
          ccpayitem: ccPayItem,
        },
      },
    };
  },
};
